// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — a lifecycle state, drawn.
//
// One circle, used wherever the app shows a state: the list rows, the detail header,
// the activity log, the transition buttons and the transition sheet. Shared so that a
// state looks the same before and after a move — the button promises exactly the paint
// the row will wear afterwards.
// ─────────────────────────────────────────────────────────────────────────────

import { stateColor } from "home/app/mobile/todos";
import { type LifecycleCategory, type LifecycleTransition, type StateIcon } from "home/app/mobile/types";

import { el } from "home/ui/mobile/dom";
import { stateGlyph } from "home/ui/mobile/icons";

/** What a circle needs to know about a state. A `Todo` is one; so is `transitionTarget()`. */
export interface DrawnState {
    status: string;
    statusCategory?: LifecycleCategory | null;
    statusColor?: string | null;
    statusIcon?: StateIcon | null;
}

/** A transition's TARGET state, in the shape a circle draws. */
export function transitionTarget(transition: LifecycleTransition): DrawnState {
    return {
        status: transition.to,
        statusCategory: transition.toCategory,
        statusColor: transition.toColor,
        statusIcon: transition.toIcon,
    };
}

/**
 * A lifecycle state as a CIRCLE holding the state's icon: icon and ring in the state's own colour
 * (else its category's), on a pale tint of it — `.pqm-state` in mobile.scss.
 *
 * ⚠ IN THE ROWS AND THE LOG THE WORD IS NOT ON SCREEN, so there it is the circle's accessible name
 * and its tooltip. It replaced a text chip whose tenant-length words ("Warten auf Rückmeldung des
 * Kunden") had to be capped and ellipsised in the row; the icon is the tenant's own choice for the
 * state, from the lifecycle designer. Where the name IS printed beside it — the detail header, the
 * transition buttons and sheet — pass `decorative`, or a screen reader says every state twice.
 *
 * A state without an icon (none set, not an `mdi:` one, or a server that predates `statusIcon`)
 * shows its initial instead of a guessed glyph — a guessed icon would say something about the
 * state that the tenant never did.
 */
export function stateDot(
    state: DrawnState,
    /** The CIRCLE's size — both 24px since 2026-09-28 (`sm` was 20px). The glyph inside it is 20; see below. */
    size: "sm" | "md" | "lg" = "md",
    /** True where the name is printed right beside the circle, so a screen reader hears it once. */
    decorative = false,
): HTMLElement {
    // Neither colour nor category is a real case — a history entry whose endpoint the tenant
    // never described — and stateColor() answers it with the neutral colour, not a guess.
    const color = stateColor(state.statusCategory, state.statusColor);
    const status = state.status;
    const dot = el("span", {
        class: `pqm-state pqm-state-${size}`,
        role: decorative ? null : "img",
        "aria-label": decorative ? null : status,
        "aria-hidden": decorative ? "true" : null,
        title: decorative ? null : status,
        // Only the colour is inline; the stylesheet draws icon, ring and tint from it per theme.
        style: `--state:${color}`,
    });

    if (state.statusIcon && state.statusIcon.d) {
        // ⚠ ONE SIZE, AND IT IS DELIBERATELY BIGGER THAN ITS CIRCLE'S CLEARANCE (2026-09-23). It was
        // 13 in the small circle and 15 in the big one; at that size a tenant's icon was a smudge on
        // a phone, which is the whole reason the word left these rows. 20 fills the 24px circle to
        // within 2px.
        //
        // What that costs, measured against the MDI font rather than guessed: a typical glyph inks
        // only ~83% of its box (`check-circle`, `progress-clock`, `help-circle`, `progress-alert`),
        // so 20 draws ~16.7px and still clears the 1.5px ring in the big circle. `block-helper` —
        // "Blocked", which real tenants use — inks 100%, edge to edge, so in the SMALL circle, while it
        // was 20px, it covered the ring and its corners sat outside the curve. That is why
        // `.pqm-state-sm` became 24px too (2026-09-28): the fix is a bigger circle, never a smaller glyph.
        // `lg` is the detail screen's status control (32px), where the glyph grows with it.
        dot.appendChild(stateGlyph(state.statusIcon, size === "lg" ? 26 : 20, "currentColor"));
    } else {
        dot.appendChild(el("span", { class: "pqm-state-initial", "aria-hidden": "true", text: initialOf(status) }));
    }

    return dot;
}

/** The first letter of a state's name, for a state with no icon. Code points, so an emoji is whole. */
function initialOf(status: string): string {
    const first = Array.from(status.trim())[0];
    return first ? first.toLocaleUpperCase() : "?";
}
